import {createAsyncThunk, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {startLoading, stopLoading} from 'app/store/utilSlice';
import {apiGet, apiPut} from 'app/shared-components/util/restAPI';

const publicArticleAdapter = createEntityAdapter({
	sortComparer: (a, b) => (b.publishDate || b.createdAt) - (a.publishDate || a.createdAt)
});

const featuredArticleAdapter = createEntityAdapter({
	sortComparer: (a, b) => (b.publishDate || b.createdAt) - (a.publishDate || a.createdAt)
});

export const fetchPublicArticle = createAsyncThunk(
  "marketing/publicArticles/fetchPublicArticle",
  async (slugOrId, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI3';
	  const path = '/x3/article/public/byslug?slug=' + encodeURIComponent(slugOrId);
      const options = {
		  headers: {},
		  response: false,
	  };

	  const data = await apiGet(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchFeaturedArticles = createAsyncThunk(
  "marketing/publicArticles/fetchFeaturedArticles",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  featured: true,
			  limit: 9,
			  offset: 0
		  },
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const searchPublicArticles = createAsyncThunk(
  "marketing/publicArticles/searchPublicArticles",
  async (params, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  keywords: params.keywords || params.s || '',
			  limit: params.limit || 9,
			  offset: params.offset || 0,
			  s: undefined,
			  ...params

		  },
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return { data: data.data || data, loadMore: params.loadMore };
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const searchSystemArticles = createAsyncThunk(
  "marketing/publicArticles/searchSystemArticles",
  async (params, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  keywords: params.keywords || '',
			  tags: params.tags || [],
			  limit: params.limit || 9,
			  offset: params.offset || 0,
			  systemArticles: true,
			  useKeywordOnly: true,
		  },
		  response: false,
	  };
	  const data = await apiPut(apiName, path, options);
	  return { data: data.data || data, loadMore: params.loadMore };
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchSystemArticleTags = createAsyncThunk(
  "marketing/publicArticles/fetchSystemArticleTags",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articletags?scope=system';
      const options = { headers: {}, response: false };
	  return await apiGet(apiName, path, options);
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchCommunityArticleTags = createAsyncThunk(
  "marketing/publicArticles/fetchCommunityArticleTags",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articletags?scope=community';
      const options = { headers: {}, response: false };
	  return await apiGet(apiName, path, options);
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchAllArticleTags = createAsyncThunk(
  "marketing/publicArticles/fetchAllArticleTags",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articletags?scope=all';
      const options = { headers: {}, response: false };
	  return await apiGet(apiName, path, options);
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchFeaturedSystemArticles = createAsyncThunk(
  "marketing/publicArticles/fetchFeaturedSystemArticles",
  async (_, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI';
	  const path = '/search/public/articles';
      const options = {
		  headers: {},
		  body: {
			  featured: true,
			  systemArticles: true,
			  useKeywordOnly: true,
			  limit: 9,
			  offset: 0,
		  },
		  response: false,
	  };
	  return await apiPut(apiName, path, options);
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchPublicArticlesByOwner = createAsyncThunk(
  "marketing/publicArticles/fetchPublicArticlesByOwner",
  async (ownerKey, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'PublicAPI3';
	  const path = '/x3/article/public/fetchbyowner?owner=' + ownerKey;
      const options = {
		  headers: {},
		  response: false,
	  };

	  const data = await apiGet(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const updatePublicArticle = createAsyncThunk(
  "marketing/publicArticles/updatePublicArticle",
  async (updateData, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'CoreAPI3';
	  const path = '/x3/article/' + updateData.id;
      const options = {
		  headers: {},
		  body: updateData,
		  response: false,
	  };

	  const data = await apiPut(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

const publicArticlesSlice = createSlice({
  name: "marketing/publicArticles",
  initialState: {
	  articles: publicArticleAdapter.getInitialState(),
	  featuredArticles: featuredArticleAdapter.getInitialState(),
	  searchedArticles: [],
	  articlePagination: { total: 0 },
	  articlesByOwner: {},
	  slugToIdMap: {},
	  featuredOnly: true,
	  keywords: '',
	  page: 1,
	  lastSearchKey: '',
	  systemArticles: [],
	  systemArticleTags: [],
	  communityArticleTags: [],
	  allArticleTags: [],
	  featuredSystemArticles: [],
	  systemArticlesPage: 1,
	  systemArticlesKeywords: '',
	  systemArticlesSelectedTags: [],
	  systemArticlesLastSearchKey: '',
  },
  reducers: {
	  setFeaturedOnly: (state, action) => {
		  state.featuredOnly = action.payload;
		  state.page = 1;
	  },
	  setKeywords: (state, action) => {
		  state.keywords = action.payload;
		  state.page = 1;
	  },
	  setPage: (state, action) => {
		  state.page = action.payload;
	  },
	  setLastSearchKey: (state, action) => {
		  state.lastSearchKey = action.payload;
	  },
	  setSystemArticlesKeywords: (state, action) => {
		  state.systemArticlesKeywords = action.payload;
		  state.systemArticlesPage = 1;
	  },
	  setSystemArticlesPage: (state, action) => {
		  state.systemArticlesPage = action.payload;
	  },
	  setSystemArticlesSelectedTags: (state, action) => {
		  state.systemArticlesSelectedTags = action.payload;
		  state.systemArticlesPage = 1;
	  },
	  setSystemArticlesLastSearchKey: (state, action) => {
		  state.systemArticlesLastSearchKey = action.payload;
	  },
  },
  extraReducers: builder => {
    builder
    .addCase(fetchPublicArticle.fulfilled, (state, action) => {
		const article = action.payload;
		
		publicArticleAdapter.upsertOne(state.articles, article);
		
		// Always map slug to ID if slug exists
		if (article.slug) {
			state.slugToIdMap[article.slug] = article.id;
		}
    })
    .addCase(fetchFeaturedArticles.fulfilled, (state, action) => {
		featuredArticleAdapter.setAll(state.featuredArticles, action.payload);
		
		// Index slugs for featured articles
		action.payload.forEach(article => {
			if (article.slug) {
				state.slugToIdMap[article.slug] = article.id;
			}
		});
    })
    .addCase(searchPublicArticles.fulfilled, (state, action) => {
		if (action.payload.loadMore) {
			state.searchedArticles = [...state.searchedArticles, ...action.payload.data];
		} else {
			state.searchedArticles = action.payload.data;
		}
    })
    .addCase(searchSystemArticles.fulfilled, (state, action) => {
		if (action.payload.loadMore) {
			state.systemArticles = [...state.systemArticles, ...action.payload.data];
		} else {
			state.systemArticles = action.payload.data;
		}
    })
    .addCase(fetchSystemArticleTags.fulfilled, (state, action) => {
		state.systemArticleTags = (action.payload || []).map(t => t.tag);
    })
    .addCase(fetchCommunityArticleTags.fulfilled, (state, action) => {
		state.communityArticleTags = (action.payload || []).map(t => t.tag);
    })
    .addCase(fetchAllArticleTags.fulfilled, (state, action) => {
		state.allArticleTags = (action.payload || []).map(t => t.tag);
    })
    .addCase(fetchFeaturedSystemArticles.fulfilled, (state, action) => {
		state.featuredSystemArticles = action.payload || [];
    })
    .addCase(fetchPublicArticlesByOwner.fulfilled, (state, action) => {
		const ownerKey = action.meta.arg;
		state.articlesByOwner[ownerKey] = action.payload;
    })
    .addCase(updatePublicArticle.fulfilled, (state, action) => {
		const updatedArticle = action.payload;
		// Update in main articles collection
		publicArticleAdapter.upsertOne(state.articles, updatedArticle);
		// Update in featured articles collection
		featuredArticleAdapter.upsertOne(state.featuredArticles, updatedArticle);
		// Update slug mapping
		if (updatedArticle.slug) {
			state.slugToIdMap[updatedArticle.slug] = updatedArticle.id;
		}
		// Update in searched articles array
		const searchIndex = state.searchedArticles.findIndex(article => article.id === updatedArticle.id);
		if (searchIndex !== -1) {
			state.searchedArticles[searchIndex] = updatedArticle;
		}
		// Update in articlesByOwner collections
		Object.keys(state.articlesByOwner).forEach(ownerKey => {
			const ownerArticles = state.articlesByOwner[ownerKey];
			const ownerIndex = ownerArticles.findIndex(article => article.id === updatedArticle.id);
			if (ownerIndex !== -1) {
				state.articlesByOwner[ownerKey][ownerIndex] = updatedArticle;
			}
		});
    })
  }
})

const { selectById: selectPublicArticleByIdFromAdapter, selectAll: selectAllPublicArticlesFromAdapter } = publicArticleAdapter.getSelectors();
const { selectAll: selectAllFeaturedArticlesFromAdapter } = featuredArticleAdapter.getSelectors();

export const selectPublicArticleById = (articleId) => state => {
	return selectPublicArticleByIdFromAdapter(state.marketing.publicArticles.articles, articleId);
}

export const selectPublicArticleBySlugOrId = (slugOrId) => state => {
	const articles = state.marketing.publicArticles.articles;
	const slugToIdMap = state.marketing.publicArticles.slugToIdMap;
	
	// Try direct ID lookup first
	let article = selectPublicArticleByIdFromAdapter(articles, slugOrId);
	
	// If not found and we have a slug mapping, try that
	if (!article && slugToIdMap[slugOrId]) {
		article = selectPublicArticleByIdFromAdapter(articles, slugToIdMap[slugOrId]);
	}
	
	return article;
}

export const selectAllPublicArticles = () => state => {
	return selectAllPublicArticlesFromAdapter(state.marketing.publicArticles.articles);
}

export const selectAllFeaturedArticles = () => state => {
	return selectAllFeaturedArticlesFromAdapter(state.marketing.publicArticles.featuredArticles);
}

export const selectSearchedPublicArticles = () => state => {
	return state.marketing.publicArticles.searchedArticles;
}

export const selectPublicArticlePagination = () => state => {
	return state.marketing.publicArticles.articlePagination;
}

export const selectPublicArticlesByOwner = (ownerKey) => state => {
	return state.marketing.publicArticles.articlesByOwner[ownerKey] || [];
}

export const selectFeaturedOnly = () => state => {
	return state.marketing.publicArticles.featuredOnly;
}

export const selectKeywords = () => state => {
	return state.marketing.publicArticles.keywords;
}

export const selectPage = () => state => {
	return state.marketing.publicArticles.page;
}

export const selectLastSearchKey = () => state => {
	return state.marketing.publicArticles.lastSearchKey;
}

export const selectSystemArticles = () => state => state.marketing.publicArticles.systemArticles;
export const selectSystemArticleTags = () => state => state.marketing.publicArticles.systemArticleTags;
export const selectCommunityArticleTags = () => state => state.marketing.publicArticles.communityArticleTags;
export const selectAllArticleTags = () => state => state.marketing.publicArticles.allArticleTags;
export const selectFeaturedSystemArticles = () => state => state.marketing.publicArticles.featuredSystemArticles;
export const selectSystemArticlesPage = () => state => state.marketing.publicArticles.systemArticlesPage;
export const selectSystemArticlesKeywords = () => state => state.marketing.publicArticles.systemArticlesKeywords;
export const selectSystemArticlesSelectedTags = () => state => state.marketing.publicArticles.systemArticlesSelectedTags;
export const selectSystemArticlesLastSearchKey = () => state => state.marketing.publicArticles.systemArticlesLastSearchKey;

export const { setFeaturedOnly, setKeywords, setPage, setLastSearchKey,
	setSystemArticlesKeywords, setSystemArticlesPage, setSystemArticlesSelectedTags, setSystemArticlesLastSearchKey
} = publicArticlesSlice.actions;

export default publicArticlesSlice.reducer;